import { useState } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { stockApi } from '../lib/api';
import * as XLSX from 'xlsx';

function descargarPlantilla() {
  const filas = [
    { CODIGO: '', DESCRIPCION: 'Filtro de aceite Cummins', EXISTENCIA: 10, PRECIO: 45.00, TIPO: 'venta' },
    { CODIGO: '', DESCRIPCION: 'Pastillas de freno delanteras', EXISTENCIA: 5, PRECIO: 120.00, TIPO: 'venta' },
    { CODIGO: '', DESCRIPCION: 'Juego de llaves mixtas', EXISTENCIA: 1, PRECIO: 0, TIPO: 'herramienta' },
    { CODIGO: '', DESCRIPCION: 'Taladro percutor', EXISTENCIA: 1, PRECIO: 0, TIPO: 'herramienta' },
  ];
  const hoja = XLSX.utils.json_to_sheet(filas);
  hoja['!cols'] = [{ wch: 12 }, { wch: 35 }, { wch: 12 }, { wch: 12 }, { wch: 14 }];
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, 'Stock');
  XLSX.writeFile(libro, 'plantilla_stock.xlsx');
}

// Normaliza un nombre para comparar duplicados ignorando mayusculas, tildes y espacios extra.
const normalizarNombre = (s: string) =>
  (s || '').normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '').trim().toLowerCase().replace(/\s+/g, ' ');

interface ProductoExcel {
  codigo?: string;
  descripcion: string;
  existencia: number;
  precio: number;
  tipo: 'venta' | 'herramienta';
  duplicado?: 'archivo' | 'sistema' | null;
}

const ImportarStock = () => {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [procesando, setProcesando] = useState(false);
  const [productos, setProductos] = useState<ProductoExcel[]>([]);
  const [resultado, setResultado] = useState<{
    exitosos: number;
    fallidos: number;
    errores: string[];
    duplicadosActualizados: string[];
  } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension !== 'xlsx' && extension !== 'xls') {
        toast.error('Por favor selecciona un archivo Excel (.xlsx o .xls)');
        return;
      }
      setArchivo(file);
      leerArchivo(file);
    }
  };

  const leerArchivo = async (file: File) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      // Leer con header para detectar columnas correctamente
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        raw: false,
        defval: ''
      });

      console.log('📊 Total filas leídas:', jsonData.length);
      console.log('📋 Primera fila:', jsonData[0]);
      console.log('🔑 Columnas detectadas:', jsonData[0] ? Object.keys(jsonData[0]) : 'ninguna');

      // Procesar datos del Excel con búsqueda flexible de columnas
      const productosImportados: ProductoExcel[] = jsonData
        .map((row: any, index: number) => {
          // Obtener todas las claves de la fila
          const keys = Object.keys(row);
          
          console.log(`Fila ${index}:`, row);
          
          // Limpiar nombres de columnas (eliminar espacios, caracteres especiales)
          const cleanKey = (k: string) => k.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
          
          // Buscar columnas por nombre limpio
          const codigoKey = keys.find(k => {
            const clean = cleanKey(k);
            return clean === 'codigo' || clean === 'code' || clean === 'cod';
          });
          
          const descripcionKey = keys.find(k => {
            const clean = cleanKey(k);
            return clean === 'descripcion' || clean === 'nombre' || clean === 'producto' || clean === 'description';
          });
          
          const existenciaKey = keys.find(k => {
            const clean = cleanKey(k);
            return clean === 'existencia' || clean === 'stock' || clean === 'cantidad';
          });
          
          const precioKey = keys.find(k => {
            const clean = cleanKey(k);
            return clean === 'precio' || clean === 'preciodeventa' || clean === 'preciodventa' || clean === 'price' || clean === 'valor';
          });

          const tipoKey = keys.find(k => cleanKey(k) === 'tipo');

          // Extraer valores
          const codigo = codigoKey ? String(row[codigoKey] || '').trim() : '';
          const descripcion = descripcionKey ? String(row[descripcionKey] || '').trim() : '';
          const existenciaStr = existenciaKey ? String(row[existenciaKey] || '0').trim() : '0';
          const precioStr = precioKey ? String(row[precioKey] || '0').trim() : '0';
          const tipoStr = tipoKey ? String(row[tipoKey] || '').trim().toLowerCase() : '';

          // Convertir a números (manejar comas como separador decimal; la existencia
          // admite decimales para productos que se venden por metro/cm)
          const existencia = parseFloat(existenciaStr.replace(',', '.').replace(/[^0-9.]/g, '')) || 0;
          const precio = parseFloat(precioStr.replace(',', '.')) || 0;
          const tipo: 'venta' | 'herramienta' = tipoStr.startsWith('herramient') ? 'herramienta' : 'venta';

          return {
            codigo,
            descripcion,
            existencia,
            precio,
            tipo
          };
        })
        .filter(p => {
          // Filtrar solo filas con descripción válida
          const isValid = p.descripcion && 
                 p.descripcion !== '' && 
                 p.descripcion.toLowerCase() !== 'descripcion' &&
                 p.descripcion.toLowerCase() !== 'nombre';
          
          if (!isValid) {
            console.log('❌ Fila filtrada:', p);
          }
          
          return isValid;
        });

      // Verificar integridad: nombres repetidos dentro del propio archivo, y nombres
      // que ya existen en el sistema (se van a actualizar en vez de crearse de nuevo).
      const conteoEnArchivo = new Map<string, number>();
      productosImportados.forEach(p => {
        const n = normalizarNombre(p.descripcion);
        conteoEnArchivo.set(n, (conteoEnArchivo.get(n) || 0) + 1);
      });

      let existentesPorNombre = new Set<string>();
      try {
        const existentes = await stockApi.getFull();
        existentesPorNombre = new Set((existentes || []).map((p: any) => normalizarNombre(p.nombre)));
      } catch {
        // Si falla la consulta no bloquea la carga del archivo, solo se pierde ese chequeo
      }

      const productosMarcados = productosImportados.map(p => {
        const n = normalizarNombre(p.descripcion);
        let duplicado: ProductoExcel['duplicado'] = null;
        if ((conteoEnArchivo.get(n) || 0) > 1) duplicado = 'archivo';
        else if (existentesPorNombre.has(n)) duplicado = 'sistema';
        return { ...p, duplicado };
      });

      setProductos(productosMarcados);

      const totalDuplicados = productosMarcados.filter(p => p.duplicado).length;

      if (productosMarcados.length === 0) {
        toast.error('No se encontraron productos válidos. Verifica que el Excel tenga las columnas correctas.');
      } else if (totalDuplicados > 0) {
        toast.warning(`${productosMarcados.length} productos cargados, ${totalDuplicados} repetidos (revisa la vista previa)`);
      } else {
        toast.success(`${productosMarcados.length} productos cargados del Excel`);
      }
    } catch (error) {
      console.error('Error leyendo archivo:', error);
      toast.error('Error al leer el archivo Excel');
    }
  };

  const importarASupabase = async () => {
    if (productos.length === 0) {
      toast.error('No hay productos para importar');
      return;
    }

    setProcesando(true);
    setResultado(null);

    let exitosos = 0;
    let fallidos = 0;
    const errores: string[] = [];
    const duplicadosActualizados: string[] = [];

    // Cargar productos existentes una sola vez para resolver duplicados por código
    const existentes = await stockApi.getFull();
    const porCodigo = new Map<string, any>((existentes || []).map((p: any) => [p.codigo, p]));
    // Ademas de por codigo, se busca coincidencia por nombre (normalizado) para no duplicar
    // productos que ya existen pero fueron importados/cargados sin codigo de barras.
    const porNombre = new Map<string, any>(
      (existentes || []).map((p: any) => [normalizarNombre(p.nombre), p])
    );

    for (const producto of productos) {
      try {
        // Validar campos obligatorios
        if (!producto.descripcion || producto.descripcion.trim() === '') {
          errores.push(`Producto sin descripción`);
          fallidos++;
          continue;
        }

        if (isNaN(producto.existencia) || producto.existencia < 0) {
          errores.push(`${producto.descripcion}: stock inválido`);
          fallidos++;
          continue;
        }

        if (isNaN(producto.precio) || producto.precio < 0) {
          errores.push(`${producto.descripcion}: precio inválido`);
          fallidos++;
          continue;
        }

        // Generar código si no existe
        const codigoFinal = producto.codigo && producto.codigo.trim() !== ''
          ? producto.codigo.trim()
          : `PROD-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

        const nombreNormalizado = normalizarNombre(producto.descripcion);
        const existente = porCodigo.get(codigoFinal) || porNombre.get(nombreNormalizado);

        if (existente) {
          // Actualizar producto existente (coincide por codigo o por nombre): se cuenta
          // aparte para que quede a la vista cuales filas eran repetidas, no nuevas.
          await stockApi.update(existente.id, {
            nombre: producto.descripcion,
            precio_venta: producto.precio,
            precio_compra: producto.precio * 0.7, // 70% del precio de venta
            stock: producto.existencia,
            tipo: producto.tipo
          });
          porCodigo.set(existente.codigo, existente);
          porNombre.set(nombreNormalizado, existente);
          duplicadosActualizados.push(producto.descripcion);
          exitosos++;
        } else {
          // Insertar nuevo producto
          const creado = await stockApi.create({
            codigo: codigoFinal,
            nombre: producto.descripcion,
            categoria: 'repuestos',
            precio_venta: producto.precio,
            precio_compra: producto.precio * 0.7,
            stock: producto.existencia,
            stock_minimo: 5,
            unidad: 'unidad',
            tipo: producto.tipo
          });
          porCodigo.set(codigoFinal, creado);
          porNombre.set(nombreNormalizado, creado);
          exitosos++;
        }

      } catch (error: any) {
        console.error('Error importando producto:', error);
        errores.push(`${producto.descripcion}: ${error.message}`);
        fallidos++;
      }
    }

    setResultado({ exitosos, fallidos, errores, duplicadosActualizados });
    setProcesando(false);

    if (exitosos > 0) {
      toast.success(`✅ ${exitosos} productos importados exitosamente`);
    }
    if (duplicadosActualizados.length > 0) {
      toast.warning(`🔁 ${duplicadosActualizados.length} eran repetidos y se actualizaron en vez de duplicarse`);
    }
    if (fallidos > 0) {
      toast.error(`❌ ${fallidos} productos fallaron`);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Importar Stock desde Excel</h1>
          <p className="text-gray-600 text-sm mt-1">
            Sirve tanto para mercadería de venta como para herramientas del taller — la columna TIPO decide cuál es cuál.
          </p>
        </div>
        <button
          onClick={descargarPlantilla}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
        >
          <Download size={16} />
          Descargar Plantilla
        </button>
      </div>

      {/* Área de carga de archivo */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <label
          htmlFor="file-upload"
          className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            {archivo ? (
              <>
                <FileSpreadsheet size={48} className="text-green-500 mb-3" />
                <p className="text-lg font-semibold text-gray-700">{archivo.name}</p>
                <p className="text-sm text-gray-500 mt-2">{productos.length} productos detectados</p>
              </>
            ) : (
              <>
                <Upload size={48} className="text-gray-400 mb-3" />
                <p className="text-lg font-semibold text-gray-700">
                  Click para seleccionar archivo Excel
                </p>
                <p className="text-sm text-gray-500 mt-2">o arrastra y suelta aquí</p>
                <p className="text-xs text-gray-400 mt-2">Formatos: .xlsx, .xls</p>
              </>
            )}
          </div>
          <input
            id="file-upload"
            type="file"
            className="hidden"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />
        </label>

        {archivo && (
          <div className="mt-4 flex gap-3">
            <button
              onClick={importarASupabase}
              disabled={procesando || productos.length === 0}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
            >
              {procesando ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Importando...
                </>
              ) : (
                <>
                  <Upload size={20} />
                  Importar {productos.length} productos
                </>
              )}
            </button>
            <button
              onClick={() => {
                setArchivo(null);
                setProductos([]);
                setResultado(null);
              }}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Limpiar
            </button>
          </div>
        )}
      </div>

      {/* Instrucciones */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">📋 Formato del Excel:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>CODIGO</strong>: Código del producto (opcional, se genera automático si está vacío)</li>
          <li>• <strong>DESCRIPCION</strong>: Nombre del producto (obligatorio)</li>
          <li>• <strong>EXISTENCIA</strong>: Cantidad en stock (obligatorio)</li>
          <li>• <strong>PRECIO</strong>: Precio de venta (obligatorio; para herramientas se puede dejar en 0)</li>
          <li>• <strong>TIPO</strong>: escribí <code>venta</code> o <code>herramienta</code> — decide si aparece en Stock o en Herramientas. Si se deja vacío, se importa como venta.</li>
        </ul>
        <p className="text-xs text-blue-700 mt-3">
          ℹ️ Si el código ya existe, o si no hay código pero el nombre coincide con un producto existente, se actualiza ese producto (incluyendo el tipo) en vez de duplicarlo. Si no coincide ninguno, se crea uno nuevo. Podés mezclar filas de venta y de herramienta en el mismo archivo.
        </p>
      </div>

      {/* Vista previa de productos */}
      {productos.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Vista Previa ({productos.length} productos)</h3>

          {productos.some(p => p.duplicado) && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4 text-sm text-yellow-800">
              ⚠️ {productos.filter(p => p.duplicado).length} producto(s) repetido(s), marcados en amarillo abajo:
              <strong> "Repetido"</strong> aparece más de una vez en este mismo archivo,
              <strong> "Ya existe"</strong> ya está cargado en el sistema. Al importar, esos no se duplican: se actualiza el que ya existe.
            </div>
          )}

          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Código</th>
                  <th className="px-4 py-2 text-left">Descripción</th>
                  <th className="px-4 py-2 text-center">Tipo</th>
                  <th className="px-4 py-2 text-right">Existencia</th>
                  <th className="px-4 py-2 text-right">Precio</th>
                  <th className="px-4 py-2 text-center">Repetido</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {productos.slice(0, 100).map((prod, idx) => (
                  <tr key={idx} className={`hover:bg-gray-50 ${prod.duplicado ? 'bg-yellow-50' : ''}`}>
                    <td className="px-4 py-2 text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {prod.codigo || <span className="text-gray-400 italic">auto</span>}
                    </td>
                    <td className="px-4 py-2">{prod.descripcion}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${prod.tipo === 'herramienta' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                        {prod.tipo === 'herramienta' ? 'Herramienta' : 'Venta'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">{prod.existencia}</td>
                    <td className="px-4 py-2 text-right font-semibold text-green-600">
                      S/ {prod.precio.toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {prod.duplicado === 'archivo' && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-200 text-yellow-900">Repetido</span>
                      )}
                      {prod.duplicado === 'sistema' && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-200 text-yellow-900">Ya existe</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {productos.length > 100 && (
              <p className="text-center text-gray-500 text-sm py-3">
                ... y {productos.length - 100} productos más
              </p>
            )}
          </div>
        </div>
      )}

      {/* Resultado de la importación */}
      {resultado && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Resultado de la Importación</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <CheckCircle size={20} />
                <span className="font-semibold">Exitosos</span>
              </div>
              <p className="text-3xl font-bold text-green-600">{resultado.exitosos}</p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700 mb-1">
                <AlertCircle size={20} />
                <span className="font-semibold">Fallidos</span>
              </div>
              <p className="text-3xl font-bold text-red-600">{resultado.fallidos}</p>
            </div>
          </div>

          {resultado.duplicadosActualizados.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-yellow-900 mb-2">
                🔁 {resultado.duplicadosActualizados.length} repetido(s) — no se duplicaron, se actualizó el producto existente:
              </h4>
              <ul className="text-sm text-yellow-800 space-y-1 max-h-48 overflow-y-auto">
                {resultado.duplicadosActualizados.map((nombre, idx) => (
                  <li key={idx}>• {nombre}</li>
                ))}
              </ul>
            </div>
          )}

          {resultado.errores.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-900 mb-2">Errores encontrados:</h4>
              <ul className="text-sm text-red-800 space-y-1 max-h-48 overflow-y-auto">
                {resultado.errores.map((error, idx) => (
                  <li key={idx}>• {error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImportarStock;
